import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'
import type { ConnectAccountsFormData, StoredOAuthState } from '@/types/digital-coworker'

export type { ConnectAccountsFormData, StoredOAuthState }

const STORAGE_KEY = 'digital_coworker_google_oauth_state'

/**
 * Store OAuth state before redirecting to Google OAuth.
 * Caller must pass the same `state` in the authorization URL so the callback can verify
 * the response belongs to this request (CSRF protection).
 * formData is optional; when provided, the callback will pass it back to ConnectAccounts.
 */
export function storeDigitalCoworkerOAuthState(
  coworkerId: string,
  state: string,
  formData?: ConnectAccountsFormData
): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ coworkerId, state, formData: formData ?? {} }))
}

/**
 * Retrieve and clear the stored OAuth state. Returns null if missing or invalid.
 */
function popDigitalCoworkerOAuthState(): StoredOAuthState | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredOAuthState
    if (typeof parsed.coworkerId === 'string' && typeof parsed.state === 'string') {
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

const DigitalCoworkerGoogleCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasLinkedRef = useRef(false)

  const code = searchParams.get('code')
  const stateFromUrl = searchParams.get('state')

  useEffect(() => {
    if (hasLinkedRef.current) return

    if (!code) {
      toast.error(t('digital_coworker:google_callback.error_auth'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    const stored = popDigitalCoworkerOAuthState()
    if (!stored) {
      toast.error(t('digital_coworker:google_callback.error_state'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    if (stateFromUrl !== stored.state) {
      toast.error(t('digital_coworker:google_callback.error_state'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    const { coworkerId } = stored
    hasLinkedRef.current = true
    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-google`

    const formData = stored.formData ?? {}

    apiService
      .linkDigitalCoworkerGoogleAccount(coworkerId, code, redirectUri)
      .then(({ email }) => {
        toast.success(t('digital_coworker:google_callback.success', { email }))
        navigate(`/digital-coworker/connect-accounts`, {
          replace: true,
          state: {
            coworkerId, formData,
            googleConnected: true, googleEmail: email,
            oneDriveConnected: formData.oneDriveConnected ?? false,
            oneDriveEmail: formData.oneDriveEmail ?? null,
          },
        })
      })
      .catch((error: unknown) => {
        console.error('Error linking Google account:', error)
        toast.error(t('digital_coworker:google_callback.error_link'))
        navigate(`/digital-coworker/connect-accounts`, {
          replace: true,
          state: {
            coworkerId, formData,
            googleConnected: false,
            oneDriveConnected: formData.oneDriveConnected ?? false,
            oneDriveEmail: formData.oneDriveEmail ?? null,
          },
        })
      })
  }, [code, stateFromUrl, navigate, t])

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <p className="text-gray-600 dark:text-gray-400">{t('digital_coworker:google_callback.connecting')}</p>
      </div>
    </div>
  )
}

export default DigitalCoworkerGoogleCallback
